import {useState} from 'react';

import {assert} from 'Asserts';
import {BioentitySummarySearchResponse, listBioentitiesWithSummaries} from 'bioentities/BioentityActions';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as RegistryUtil from 'registry/RegistryUtil';
import {searchStorablesWithSummaries, StorableSummarySearchResponse} from 'samples/StorableActions';
import {FilterKey, Operator, SearchParams, SearchResults, SortKey} from 'search/SearchConstants';
import {HookSetState} from 'util/Types';
import {WorklistType} from 'worklists/WorklistConstants';

import {Barcodes, ScannedWorklistItem, ScannedWorklistItemsByIdentifier} from './BarcodeConstants';

const FetchChunkSize = 100;

function getWorklistItemSearchFunction(
  worklistType: WorklistType
): (searchParams: SearchParams) => Promise<SearchResults> {
  assert(
    [WorklistType.CONTAINER, WorklistType.PLATE, WorklistType.BIOENTITY].includes(worklistType),
    'Only storable and bioentity scanning is supported'
  );
  if (worklistType === WorklistType.BIOENTITY) {
    return listBioentitiesWithSummaries;
  } else {
    return (searchParams) => searchStorablesWithSummaries(searchParams).responsePromise;
  }
}

function searchResultsAreStorables(
  searchResults: SearchResults
): searchResults is StorableSummarySearchResponse {
  return (searchResults as StorableSummarySearchResponse).storableSummaries !== undefined;
}

function searchResultsAreBioentities(
  searchResults: SearchResults
): searchResults is BioentitySummarySearchResponse {
  return (searchResults as BioentitySummarySearchResponse).fileSummariesById !== undefined;
}

function getWorklistItemScannedIdentifier(searchResults: SearchResults, apiId: string): string | null {
  let worklistItemSummary;
  let scannedIdentifier = null;
  if (searchResultsAreStorables(searchResults)) {
    [worklistItemSummary] = searchResults.storableSummaries.filter((summary) => summary.id === apiId);
    scannedIdentifier = worklistItemSummary.barcode!;
  } else if (searchResultsAreBioentities(searchResults)) {
    worklistItemSummary = searchResults.fileSummariesById[apiId];
    scannedIdentifier = RegistryUtil.getPrimaryRegistryIdentifier(worklistItemSummary);
  } else {
    assert(false, 'Search results were of an unexpected type');
  }
  // search by barcode is case-insensitive, and all user-entered barcodes are mapped to lower case in useBarcodes
  return scannedIdentifier ? scannedIdentifier.toLowerCase() : null;
}

function worklistTypeToModelTypes(worklistType: WorklistType): Array<string> {
  switch (worklistType) {
    case WorklistType.CONTAINER:
      return [ModelTypeConstants.CONTAINER];
    case WorklistType.PLATE:
      return [ModelTypeConstants.PLATE];
    case WorklistType.BIOENTITY:
      return [
        ModelTypeConstants.PROTEIN,
        ModelTypeConstants.SEQUENCE,
        ModelTypeConstants.DNA_OLIGO,
        ModelTypeConstants.BASIC_FOLDER_ITEM,
        ModelTypeConstants.MIXTURE,
      ];
    default:
      throw new Error(`Unexpected worklist type: ${worklistType}`);
  }
}

function worklistTypeToIdentifierFilter(worklistType: WorklistType): FilterKey {
  switch (worklistType) {
    case WorklistType.CONTAINER:
    case WorklistType.PLATE:
      return FilterKey.BARCODES;
    case WorklistType.BIOENTITY:
      // NOTE: This filter will include matches by name or alias, which are not of interest here. However, we handle
      // correctly skipping these matches by only getting the registry ID in getWorklistItemScannedIdentifier,
      // and then only selecting fetched items whose identifier matches the original scanned barcodes when
      // building updatedWorklistItemsByScannedIdentifier.
      return FilterKey.BIOENTITY_LABELS;
    default:
      throw new Error(`Unexpected worklist type: ${worklistType}`);
  }
}

/**
 * Search for storables that match the given barcodes. Updates fetchedWorklistItemsByIdentifier with results.
 */
async function fetchFromScannedBarcodes(
  scannedBarcodes: Array<string>,
  setFetchedWorklistItemsByScannedIdentifier: HookSetState<ScannedWorklistItemsByIdentifier>,
  worklistType: WorklistType
): Promise<void> {
  const searchFunction = getWorklistItemSearchFunction(worklistType);
  const searchResults = await searchFunction({
    offset: 0,
    limit: FetchChunkSize,
    sorts: [{key: SortKey.SORT_MODIFIED_AT_KEY, reverse: false}],
    query: '',
    filters: [
      {
        key: FilterKey.TYPES,
        operator: Operator.IS_ONE_OF,
        value: worklistTypeToModelTypes(worklistType),
      },
      {
        // @ts-expect-error: This should be fixed by adding `FilterKey.TAG` to a member of the `Filter` type
        // in `SearchConstants.ts`. Since it's not currently, you can't assign type FilterKey (the output of
        // worklistTypeToIdentifierFilter) to filters[number]['key']
        key: worklistTypeToIdentifierFilter(worklistType),
        value: scannedBarcodes,
        operator: Operator.IS_ONE_OF,
      },
    ],
  });

  const fetchedWorklistItemApiIdsByScannedIdentifier: {[scannedBarcode: string]: Array<string>} = {};
  searchResults.ids.forEach((apiId) => {
    const scannedIdentifier = getWorklistItemScannedIdentifier(searchResults, apiId);
    if (scannedIdentifier) {
      if (!fetchedWorklistItemApiIdsByScannedIdentifier.hasOwnProperty(scannedIdentifier)) {
        fetchedWorklistItemApiIdsByScannedIdentifier[scannedIdentifier] = [];
      }
      fetchedWorklistItemApiIdsByScannedIdentifier[scannedIdentifier].push(apiId);
    }
  });
  setFetchedWorklistItemsByScannedIdentifier((currentWorklistItemsByScannedIdentifier) => {
    const updatedWorklistItemsByScannedIdentifier = new Map(currentWorklistItemsByScannedIdentifier);
    for (const scannedBarcode of scannedBarcodes) {
      const matchedWorklistItemApiIds = fetchedWorklistItemApiIdsByScannedIdentifier[scannedBarcode];
      if (matchedWorklistItemApiIds && matchedWorklistItemApiIds.length) {
        updatedWorklistItemsByScannedIdentifier.set(
          scannedBarcode,
          matchedWorklistItemApiIds.map((apiId) => ({
            scannedIdentifier: scannedBarcode,
            apiId,
            notFound: false,
          }))
        );
      } else {
        updatedWorklistItemsByScannedIdentifier.set(scannedBarcode, [
          {scannedIdentifier: scannedBarcode, apiId: null, notFound: true},
        ]);
      }
    }
    return updatedWorklistItemsByScannedIdentifier;
  });
}

interface UseWorklistItemsFromBarcodeScanning {
  visibleScannedWorklistItems: Array<ScannedWorklistItem>;
  removeScannedWorklistItem: (scannedWorklistItem: ScannedWorklistItem) => void;
  hasVisibleDuplicateMatches: boolean;
}

/**
 * Custom React hook to resolve barcodes to worklist items. Represents what has already been fetched, and does not
 * remove items from the map if a barcode is removed (removed state is instead tracked separately).
 */
export default function useWorklistItemsFromBarcodeScanning(
  desiredBarcodes: Barcodes,
  worklistType: WorklistType
): UseWorklistItemsFromBarcodeScanning {
  const [fetchedWorklistItemsByScannedIdentifier, setFetchedWorklistItemsByScannedIdentifier] =
    useState<ScannedWorklistItemsByIdentifier>(new Map<string, Array<ScannedWorklistItem>>());
  const [removedWorklistItems, setRemovedWorklistItems] = useState<Array<ScannedWorklistItem>>([]);

  // Fetch the worklist items for any new barcodes
  const newBarcodes = Array.from(desiredBarcodes).filter(
    (barcode) => !fetchedWorklistItemsByScannedIdentifier.has(barcode)
  );
  for (let i = 0; i < newBarcodes.length; i += FetchChunkSize) {
    const newBarcodesChunk = newBarcodes.slice(i, i + FetchChunkSize);
    fetchFromScannedBarcodes(newBarcodesChunk, setFetchedWorklistItemsByScannedIdentifier, worklistType);
  }

  // Remove items from removedWorklistItems if they correspond to a barcode that the user has re-entered
  const barcodesWereReadded = removedWorklistItems.some((worklistItem) =>
    desiredBarcodes.has(worklistItem.scannedIdentifier)
  );
  if (barcodesWereReadded) {
    // It's important to only call setRemovedWorklistItems if barcodes were re-added; otherwise we'll trigger
    // an infinite render loop
    setRemovedWorklistItems((currentRemovedItems) =>
      currentRemovedItems.filter((removedItem) => !desiredBarcodes.has(removedItem.scannedIdentifier))
    );
  }

  // Compute which barcode items are currently visible, and whether there are any duplicate matches for a
  // single barcode within the visible items
  const visibleScannedWorklistItems = [];
  for (const worklistItems of fetchedWorklistItemsByScannedIdentifier.values()) {
    // NOTE: It's important to add to visibleScannedWorklistItems in insertion order of fetchedWorklistItemsByIdentifier,
    // because that preserves the ordering in which the user entered the barcodes
    visibleScannedWorklistItems.push(
      ...worklistItems.filter((worklistItem) => !removedWorklistItems.includes(worklistItem))
    );
  }
  for (const barcode of newBarcodes) {
    visibleScannedWorklistItems.push({scannedIdentifier: barcode, apiId: null, notFound: false});
  }
  const visibleBarcodes = visibleScannedWorklistItems.map(
    (scannedWorklistItem) => scannedWorklistItem.scannedIdentifier
  );

  return {
    visibleScannedWorklistItems,
    removeScannedWorklistItem: (removedItem: ScannedWorklistItem) => {
      setRemovedWorklistItems((worklistItems) => [...worklistItems, removedItem]);
    },
    hasVisibleDuplicateMatches: visibleBarcodes.length !== new Set(visibleBarcodes).size,
  };
}
